/**
 * RdsCard — full detail card for RDS instance in Resource Grid view.
 */
import type { RdsInstance } from '@/types';
import { StatusBadge } from '../primitives/StatusBadge';
import { MetricGauge } from '../primitives/MetricGauge';
import { mapRdsStatusToHealth } from './RdsCompactRow';
import styles from './RdsCard.module.css';

export interface RdsCardProps {
  instance: RdsInstance;
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return 'N/A';
  const gb = bytes / (1024 ** 3);
  return `${gb.toFixed(1)} GB`;
}

export function RdsCard({ instance }: RdsCardProps) {
  const health = mapRdsStatusToHealth(instance.dbStatus);

  return (
    <article
      className={styles.card}
      aria-label={`RDS instance ${instance.dbIdentifier}`}
    >
      <header className={styles.header}>
        <StatusBadge status={health} size="md" />
        <span className={styles.badge}>RDS</span>
      </header>

      <div className={styles.body}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>DB Identifier</span>
          <span className={styles.fieldValue} title={instance.dbIdentifier}>
            {instance.dbIdentifier}
          </span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Engine</span>
          <span className={styles.fieldValue}>
            {instance.engine} {instance.engineVersion}
          </span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Class</span>
          <span className={styles.fieldValue}>{instance.dbInstanceClass}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Status</span>
          <span className={styles.fieldValue}>{instance.dbStatus}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Multi-AZ</span>
          <span className={styles.fieldValue}>{instance.multiAz ? 'Yes' : 'No'}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Free Storage</span>
          <span className={styles.fieldValue}>{formatBytes(instance.freeStorageBytes)}</span>
        </div>
        {instance.connectionCount !== null && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Connections</span>
            <span className={styles.fieldValue}>{instance.connectionCount}</span>
          </div>
        )}
      </div>

      <div className={styles.gauges}>
        <MetricGauge label="CPU" value={instance.cpuUtilization} warnThreshold={70} critThreshold={90} />
      </div>
    </article>
  );
}
